import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import type { CartPublic, ProductPublic, ProviderPublic, QuoteFormField } from "@dugads/shared";
import * as api from "@/lib/api";
import { safeHref } from "@/lib/safe-url";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { CheckCircle2, ExternalLink, Minus, Plus } from "lucide-react";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

export default function ShopProviderPage() {
  const { slug } = useParams<{ slug: string }>();
  const [provider, setProvider] = useState<ProviderPublic | null>(null);
  const [products, setProducts] = useState<ProductPublic[]>([]);
  const [cart, setCart] = useState<CartPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshCart = useCallback(async (providerId: string) => {
    const r = await api.getShopCart(providerId);
    setCart(r.cart);
  }, []);

  useEffect(() => {
    if (!slug) return;
    api.getShopProvider(slug)
      .then(async (r) => {
        setProvider(r.provider);
        setProducts(r.products);
        // external_redirect providers have no cart surface.
        if (r.provider.fulfillment_type === "order_email") {
          await refreshCart(r.provider.id);
        }
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [slug, refreshCart]);

  async function setQuantity(product: ProductPublic, quantity: number) {
    if (!provider) return;
    try {
      const r = await api.upsertCartItem({
        provider_id: provider.id,
        product_id: product.id,
        quantity: Math.max(0, quantity),
      });
      setCart(r.cart);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update cart");
    }
  }

  function qtyInCart(productId: string): number {
    return cart?.items.find((it) => it.product_id === productId)?.quantity ?? 0;
  }

  async function handleRedirect() {
    if (!provider) return;
    try {
      const r = await api.resolveProviderRedirect(provider.slug, { platform: "web" });
      const target = safeHref(r.redirect_url);
      if (!target) {
        setError("This provider returned an invalid redirect URL.");
        return;
      }
      window.open(target, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to open provider");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="shop-provider" data-state="loading">Loading…</p>;
  if (error || !provider) {
    return (
      <div data-page="shop-provider" data-state="error" className="flex flex-col gap-4">
        <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }]} />
        <p className="text-sm text-destructive" data-error>{error ?? "Provider not found"}</p>
      </div>
    );
  }

  // 093 -- quote view
  if (provider.fulfillment_type === "quote") {
    return <QuoteForm provider={provider} />;
  }

  // 096 -- cashback view
  if (provider.fulfillment_type === "cashback") {
    return <CashbackLanding provider={provider} />;
  }

  // 092 -- external_redirect view (no catalog, single CTA)
  if (provider.fulfillment_type === "external_redirect") {
    return (
      <div data-page="shop-provider" data-provider-type="external_redirect" data-state="ready" className="flex flex-col gap-6">
        <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: provider.name }]} />
        <div className="flex items-center gap-3">
          {provider.logo_url && <img src={provider.logo_url} alt="" className="h-12 w-auto object-contain" />}
          <h1 className="text-2xl font-semibold">{provider.name}</h1>
        </div>
        <Card>
          <CardContent className="flex flex-col gap-4 p-6">
            {provider.discount_blurb && (
              <p className="text-lg font-semibold text-primary" data-field="discount-blurb">{provider.discount_blurb}</p>
            )}
            {provider.description && (
              <p className="max-w-2xl whitespace-pre-wrap text-sm text-muted-foreground">{provider.description}</p>
            )}
            <p className="text-xs text-muted-foreground">
              You'll be taken to {provider.name}'s website. Delivery, payment, and support are handled by them.
            </p>
            <div>
              <Button onClick={handleRedirect} data-action="external-redirect" className="inline-flex items-center gap-2">
                {provider.external_cta_label || `Shop ${provider.name}`}
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-page="shop-provider" data-provider-type="order_email" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: provider.name }]} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            {provider.logo_url && <img src={provider.logo_url} alt="" className="h-10 w-auto object-contain" />}
            <h1 className="text-2xl font-semibold">{provider.name}</h1>
          </div>
          {provider.description && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{provider.description}</p>}
        </div>
        {cart && cart.item_count > 0 && (
          <Card className="sticky top-4" data-region="cart-summary">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="text-sm">
                <p className="font-medium">{cart.item_count} item{cart.item_count === 1 ? "" : "s"}</p>
                <p className="text-muted-foreground">{fmtMoney(cart.subtotal_cents, cart.currency)}</p>
              </div>
              <Link to={`/shop/${provider.slug}/cart`} className={buttonVariants({ size: "sm" })} data-action="view-cart">View cart</Link>
            </CardContent>
          </Card>
        )}
      </div>

      {products.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No products available.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-region="products">
          {products.map((p) => {
            const q = qtyInCart(p.id);
            return (
              <Card key={p.id} data-item="product" data-item-id={p.id}>
                <CardContent className="flex flex-col gap-3 p-4">
                  {p.image_url && <img src={p.image_url} alt="" className="h-32 w-full object-contain" />}
                  <div>
                    <h2 className="text-base font-semibold">{p.name}</h2>
                    {p.description && <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{fmtMoney(p.price_cents, p.currency)}</span>
                    {q === 0 ? (
                      <Button size="sm" onClick={() => setQuantity(p, 1)} data-action="add-to-cart">Add</Button>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setQuantity(p, q - 1)} data-action="decrement" aria-label="Decrease quantity">
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm font-medium tabular-nums" data-field="quantity">{q}</span>
                        <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setQuantity(p, q + 1)} data-action="increment" aria-label="Increase quantity">
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 093 -- QuoteForm renders the declarative form config, handles submit, shows success.
function QuoteForm({ provider }: { provider: ProviderPublic }) {
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState<QuoteFormField[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    { success: boolean; message: string | null; support_code?: string } | null
  >(null);
  // 099 -- friendly dialog shown on unrecoverable 500 errors (handler threw,
  // missing provider config, etc.). Carries the support_code for admin
  // cross-reference. Separate from `error` which is the inline validation
  // message shown below the form on 4xx responses.
  const [failureDialog, setFailureDialog] = useState<
    { message: string; supportCode?: string } | null
  >(null);

  useEffect(() => {
    api.getQuoteForm(provider.slug)
      .then((r) => {
        setFields(r.form.fields);
        setValues({ ...r.prefill });
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load form"))
      .finally(() => setLoading(false));
  }, [provider.slug]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFailureDialog(null);
    setSubmitting(true);
    try {
      const r = await api.submitQuote(provider.slug, { form: values });
      setResult({ success: r.success, message: r.message, support_code: r.support_code });
    } catch (err) {
      if (err instanceof api.ApiError) {
        // 4xx → inline validation error under the fields. 5xx → friendly
        // dialog with support_code so admin can trace via TelemetryEvent.
        if (err.status >= 500) {
          const supportCode =
            typeof err.body?.support_code === "string" ? err.body.support_code : undefined;
          setFailureDialog({ message: err.message, supportCode });
        } else {
          setError(err.message);
        }
      } else {
        setError("Submission failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div data-page="shop-provider" data-provider-type="quote" data-state="submitted" className="flex flex-col gap-6">
        <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: provider.name }]} />
        <Card className="border-primary">
          <CardContent className="flex flex-col items-start gap-3 p-6">
            <CheckCircle2 className="h-10 w-10 text-primary" />
            <h1 className="text-xl font-semibold">{result.success ? "Request submitted" : "Thanks -- we'll keep trying"}</h1>
            <p className="text-sm text-muted-foreground" data-field="result-message">
              {result.message ?? (result.success ? "We've passed your details to the provider." : "Our servers couldn't reach the provider right now. We'll retry in the background.")}
            </p>
            {result.support_code && !result.success && (
              <p className="text-xs text-muted-foreground" data-field="support-code">
                Support reference:{" "}
                <span className="font-mono font-semibold">{result.support_code}</span>
              </p>
            )}
            <Link to="/shop" className={buttonVariants({ size: "sm", variant: "outline" })} data-action="back-to-shop">Back to shop</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-page="shop-provider" data-provider-type="quote" data-state={loading ? "loading" : "ready"} className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: provider.name }]} />
      <div className="flex items-center gap-3">
        {provider.logo_url && <img src={provider.logo_url} alt="" className="h-12 w-auto object-contain" />}
        <h1 className="text-2xl font-semibold">{provider.name}</h1>
      </div>
      {provider.description && (
        <p className="max-w-2xl whitespace-pre-wrap text-sm text-muted-foreground">{provider.description}</p>
      )}
      {loading ? (
        <p className="text-muted-foreground" data-state="loading">Loading form…</p>
      ) : (
        <Card className="max-w-xl">
          <CardHeader><CardTitle className="text-base">Get a quote</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="quote">
              {fields.map((f) => (
                <div key={f.name} className="flex flex-col gap-2">
                  <Label htmlFor={`q-${f.name}`}>
                    {f.label}
                    {f.required && <span className="ml-1 text-xs text-destructive">*</span>}
                  </Label>
                  <QuoteFieldInput field={f} value={values[f.name] ?? ""} onChange={(v) => setValues({ ...values, [f.name]: v })} />
                </div>
              ))}
              {error && <p className="text-sm text-destructive" data-error>{error}</p>}
              <Button type="submit" disabled={submitting} data-action="submit-quote">
                {submitting ? "Submitting…" : "Submit"}
              </Button>
              <p className="text-xs text-muted-foreground">
                Your details are passed to {provider.name} directly. Dugads stores a copy of the submission for your records but does not handle the quote itself.
              </p>
            </form>
          </CardContent>
        </Card>
      )}
      {failureDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4"
          role="dialog"
          aria-modal="true"
          data-dialog="quote-failure"
          onClick={() => setFailureDialog(null)}
        >
          <Card className="max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardContent className="flex flex-col gap-3 p-6">
              <h2 className="text-lg font-semibold" data-field="dialog-title">
                We couldn't send your request
              </h2>
              <p className="text-sm text-muted-foreground" data-field="dialog-message">
                {failureDialog.message}
              </p>
              {failureDialog.supportCode && (
                <p className="text-xs text-muted-foreground" data-field="support-code">
                  If you contact support, please quote reference:{" "}
                  <span className="font-mono font-semibold">{failureDialog.supportCode}</span>
                </p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => setFailureDialog(null)}
                  data-action="dialog-dismiss"
                >
                  OK
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function QuoteFieldInput({
  field,
  value,
  onChange,
}: {
  field: QuoteFormField;
  value: string;
  onChange: (v: string) => void;
}) {
  const common = {
    id: `q-${field.name}`,
    "data-field": field.name,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(e.target.value),
    placeholder: field.placeholder,
    required: field.required,
    maxLength: field.max_length,
  };
  if (field.type === "textarea") {
    return (
      <textarea
        {...common}
        rows={3}
        className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        aria-label={field.label}
      />
    );
  }
  if (field.type === "select") {
    return (
      <select
        {...common}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        aria-label={field.label}
      >
        <option value="">Select…</option>
        {(field.options ?? []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  const htmlType = field.type === "email" ? "email"
    : field.type === "phone" ? "tel"
    : field.type === "date" ? "date"
    : "text";
  return <Input type={htmlType} {...common} />;
}

// 096 -- Cashback landing. Shows the split, existing link state, and a
// "Link your account" button that opens a mock capture form (served by
// the portal at /shop/:slug/link/mock). Real OAuth will replace the mock
// once the provider ships their flow.
function CashbackLanding({ provider }: { provider: ProviderPublic }) {
  const [link, setLink] = useState<null | { id: string; external_account_ref_suffix: string }>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getMyCashback()
      .then((r) => {
        const active = r.links.find((l) => l.provider_id === provider.id && !l.unlinked_at);
        setLink(active ? { id: active.id, external_account_ref_suffix: active.external_account_ref_suffix } : null);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [provider.id]);

  async function handleLink() {
    try {
      const r = await api.startProviderLink(provider.slug);
      const target = safeHref(r.redirect_url);
      if (!target) {
        setError("The linking service returned an invalid redirect URL.");
        return;
      }
      window.location.href = target;
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to start linking");
    }
  }

  async function handleUnlink() {
    if (!link) return;
    if (!confirm("Unlink your account? Future spend will not earn cashback until you relink.")) return;
    try {
      await api.unlinkProvider(link.id);
      setLink(null);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to unlink");
    }
  }

  const split = provider.cashback_split;

  return (
    <div
      data-page="shop-provider"
      data-provider-type="cashback"
      data-state={loading ? "loading" : "ready"}
      className="flex flex-col gap-6"
    >
      <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: provider.name }]} />
      <div className="flex items-center gap-3">
        {provider.logo_url && <img src={provider.logo_url} alt="" className="h-12 w-auto object-contain" />}
        <h1 className="text-2xl font-semibold">{provider.name}</h1>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4 p-6">
          {provider.discount_blurb && (
            <p className="text-lg font-semibold text-primary" data-field="discount-blurb">{provider.discount_blurb}</p>
          )}
          {provider.description && (
            <p className="max-w-2xl whitespace-pre-wrap text-sm text-muted-foreground">{provider.description}</p>
          )}

          {split && (
            <div className="rounded-md border bg-muted/30 p-4 text-sm" data-region="cashback-split">
              <p className="font-medium">How it's split</p>
              <ul className="mt-2 grid grid-cols-2 gap-y-1 sm:grid-cols-3">
                <li><span className="text-muted-foreground">You:</span> <span className="font-semibold">{split.parent_pct}%</span></li>
                <li><span className="text-muted-foreground">Your school:</span> <span className="font-semibold">{split.school_pct}%</span></li>
                <li><span className="text-muted-foreground">Dugads:</span> <span className="font-semibold">{split.dugads_pct}%</span></li>
              </ul>
            </div>
          )}

          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          {!loading && !link && (
            <div>
              <Button onClick={handleLink} data-action="start-link">
                Link your {provider.name} account
              </Button>
            </div>
          )}
          {!loading && link && (
            <div className="flex flex-wrap items-center gap-3" data-region="active-link">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <p className="text-sm">
                Linked -- card ending <span className="font-mono">•••• {link.external_account_ref_suffix}</span>
              </p>
              <Button variant="outline" size="sm" onClick={handleUnlink} data-action="unlink">Unlink</Button>
              <Link to="/me/cashback" className={buttonVariants({ size: "sm" })}>View cashback</Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
